import React, { useState, useEffect, useContext } from 'react';
import { Popover } from '@nextiva/next-ui-components';
import { useSelector } from 'react-redux';

import { MultiSelectSearch } from 'components';

import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { Add } from '../../Add';
import {
  actionTypes as userActionTypes,
  UserContext,
} from '../../../../usersContext';
import {
  actionTypes as userDrawerActionTypes,
  UserDrawerContext,
} from '../../../../UserDrawerContent/UserDrawerContext';

Popover.displayName = 'ForwardRef(Modal)';

export const AddLicense = ({ unsavedUserChanges, saveChanges }) => {
  const [addLicenseOpen, setAddLicenseOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [licenseItems, setLicenseItems] = useState([]);
  const [selectedLicenses, setSelectedLicenses] = useState([]);
  const {
    featureState: { authV3Enabled, purchaseLicenseFlow },
  } = useContext(FeatureContext);
  const purchaseLicenseFlowGrantStatus = useSelector(
    (state) =>
      state.globalPolicyDecisionRequestState.purchaseLicenseFlowGrantStatus,
  );
  const accountV2Summary = useSelector(
    (state) => state.globalAccountV2SummaryState.globalAccountV2Summary,
  );
  const inAppPurchaseEnabled =
    accountV2Summary?.attributes?.inAppPurchaseEnabled === 'true' ||
    accountV2Summary?.attributes?.inAppPurchaseEnabled === true;
  const freemiumFlowEnabled =
    accountV2Summary?.attributes?.freemium === 'true' ||
    accountV2Summary?.attributes?.freemium === true;
  const isPurchaseLicenseFlowEnabled =
    purchaseLicenseFlow &&
    inAppPurchaseEnabled &&
    purchaseLicenseFlowGrantStatus &&
    !freemiumFlowEnabled;

  const licenseTooltipMessage = isPurchaseLicenseFlowEnabled
    ? 'Your account has 0 available licenses. Purchase additional licenses to add more users.'
    : 'This license has 0 available licenses to assign, call (800) 799-0600 to purchase more.';

  const {
    userState: { licenseList },
    dispatch: dispatchUser,
  } = useContext(UserContext);
  const {
    userDrawerState: { assignedLicenses, licensesToAdd },
    dispatchUserDrawer,
  } = useContext(UserDrawerContext);

  useEffect(() => {
    if (!licenseList) return;
    const licenses = licenseList.map((license) => {
      const isAssigned = assignedLicenses
        ? assignedLicenses.some((l) => l.id === license.id)
        : false;

      // If the license is not assigned and there are no more available disable it.
      const disabled =
        !isAssigned &&
        (license.number_of_licenses_available === 0 ||
          license.numberOfLicensesAvailable === 0);

      return {
        id: license.id,
        text:
          license.license_definition?.name || license.licenseDefinition.name,
        disabled,
        description:
          license?.license_definition?.description ||
          license?.licenseDefinition?.description,
      };
    });

    setLicenseItems(licenses);
  }, [assignedLicenses, licenseList]);

  useEffect(() => {
    if (!assignedLicenses) return;

    setSelectedLicenses([
      ...assignedLicenses.map((license) => license.id),
      ...licensesToAdd.map((license) => license.id),
    ]);
  }, [assignedLicenses, licensesToAdd]);

  const toggleAddLicense = (event) => {
    setAddLicenseOpen(!addLicenseOpen);
    setAnchorEl(event.currentTarget);
  };

  const handleNewLicenseClick = (id) => {
    const license = licenseList.find((l) => l.id === id);

    dispatchUserDrawer({
      type: userDrawerActionTypes.SET_LICENSES_TO_ADD,
      licensesToAdd: [...licensesToAdd, license],
    });
    dispatchUserDrawer({
      type: userDrawerActionTypes.ADD_LICENSE,
      license,
    });
  };

  const handleSelectedLicenseClick = (id) => {
    dispatchUserDrawer({
      type: userDrawerActionTypes.SET_LICENSES_TO_ADD,
      licensesToAdd: licensesToAdd.filter((l) => l.id !== id),
    });

    dispatchUserDrawer({
      type: userDrawerActionTypes.SET_ASSIGNED_LICENSES,
      assignedLicenses: assignedLicenses.filter((l) => l.id !== id),
    });

    // optimistically update license count so that the license
    // can be reapplied while drawer is open
    const licenseListCopy = licenseList.map((l) => {
      return { ...l };
    });
    const license = licenseListCopy.find((l) => l.id === id);

    if (authV3Enabled) {
      license.numberOfLicensesAssigned -= 1;
      license.numberOfLicensesAvailable += 1;
    } else {
      license.number_of_licenses_assigned -= 1;
      license.number_of_licenses_available += 1;
    }

    dispatchUser({
      type: userActionTypes.SET_LICENSE_LIST,
      licenseList: licenseListCopy,
    });
  };

  const handleAddLicenseClick = (id) => {
    if (selectedLicenses.includes(id)) {
      handleSelectedLicenseClick(id);
    } else {
      handleNewLicenseClick(id);
    }
    toggleAddLicense({});
  };

  return (
    <>
      <button
        type="button"
        className="m-user-edit-drawer-section__add"
        onClick={toggleAddLicense}
        data-testid="PlatformAdminUI-AddLicense-Button"
      >
        <Add text="license" />
      </button>
      <Popover
        open={addLicenseOpen}
        anchorEl={anchorEl}
        onClose={toggleAddLicense}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MultiSelectSearch
          title="Add Licenses"
          placeholder="Search Licenses"
          options={licenseItems}
          selectedItems={selectedLicenses}
          onClick={handleAddLicenseClick}
          tooltipMessage={licenseTooltipMessage}
          unsavedUserChanges={unsavedUserChanges}
          saveChanges={saveChanges}
          enablePurchaseLicenseLink
          data-testid="PlatformAdminUI-AddLicense-MultiSelectSearch"
        />
      </Popover>
    </>
  );
};
